import React, { useState, useRef, useEffect } from 'react';
import { EyeOpenIcon, EyeOffIcon } from '../icons/EyeIcons';

interface FormFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  rightIcon?: React.ReactNode;
  useInputBackground?: boolean; // Use regular input background even when disabled
}

function resolveBackgroundColor(disabled: boolean, useInputBackground: boolean): string {
  if (disabled) {
    return 'var(--bg-input-disabled)';
  }
  if (useInputBackground) {
    return 'var(--bg-input)';
  }
  return 'var(--bg-input-default)';
}

function resolveBorderColor(
  error: string | undefined,
  disabled: boolean,
  useInputBackground: boolean
): string {
  if (error) {
    return '#dc2626';
  }
  if (disabled) {
    return 'var(--border-input-disabled)';
  }
  if (useInputBackground) {
    return 'var(--border-input)';
  }
  return 'var(--border-input-default)';
}

function PasswordToggle({
  showPassword,
  disabled,
  onToggle,
}: Readonly<{
  showPassword: boolean;
  disabled: boolean;
  onToggle: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onToggle()}
      disabled={disabled}
      aria-label={showPassword ? 'Hide password' : 'Show password'}
      style={{
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: '4px',
        color: '#64748b',
        border: 'none',
        background: 'transparent',
      }}
    >
      {showPassword ? <EyeOpenIcon size={20} /> : <EyeOffIcon size={20} />}
    </button>
  );
}

function RightIconSlot({ rightIcon }: Readonly<{ rightIcon: React.ReactNode }>) {
  return (
    <div
      style={{
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        pointerEvents: 'none',
      }}
    >
      {rightIcon}
    </div>
  );
}

function applyAutofillStyling(
  element: HTMLInputElement,
  textColor: string,
  backgroundColor: string
): void {
  element.style.color = textColor;
  element.style.WebkitTextFillColor = textColor;
  element.style.backgroundColor = backgroundColor;
  element.style.background = backgroundColor;
  element.style.WebkitBoxShadow = `0 0 0px 1000px ${backgroundColor} inset`;
  element.style.boxShadow = `inset 0px 2px 4px 0px rgba(0,0,0,0.06), 0 0 0 1000px ${backgroundColor} inset`;
}

function useAutofillFix(
  inputRef: React.RefObject<HTMLInputElement>,
  disabled: boolean,
  backgroundColor: string,
  textColor: string
): void {
  useEffect(() => {
    const input = inputRef.current;
    if (!input || disabled) {
      return;
    }

    const checkAndFixAutofill = () => {
      if (input.matches(':-webkit-autofill')) {
        applyAutofillStyling(input, textColor, backgroundColor);
      }
    };

    checkAndFixAutofill();
    const interval = setInterval(checkAndFixAutofill, 100);
    const handleFocus = () => {
      setTimeout(checkAndFixAutofill, 0);
    };
    const handleBlur = () => {
      setTimeout(checkAndFixAutofill, 0);
    };

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);

    return () => {
      clearInterval(interval);
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
    };
  }, [disabled, backgroundColor, textColor, inputRef]);
}

function FormFieldLabel({
  label,
  required,
  disabled,
}: Readonly<{
  label: string;
  required: boolean;
  disabled: boolean;
}>) {
  const displayLabel = required && label.endsWith(' *') ? label.slice(0, -2) : label;
  return (
    <label
      style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: 500,
        color: disabled ? 'var(--label-color-disabled)' : 'var(--label-color-default)',
        lineHeight: '20px',
      }}
    >
      {displayLabel}
      {required && <span style={{ color: '#dc2626', marginLeft: '2px' }}>*</span>}
    </label>
  );
}

function FormFieldInput({
  disabled,
  isPasswordField,
  showPassword,
  type,
  paddingRight,
  backgroundColor,
  borderColor,
  textColor,
  className,
  inputRef,
  onBlur,
  autoComplete,
  props,
}: Readonly<{
  disabled: boolean;
  isPasswordField: boolean;
  showPassword: boolean;
  type: string | undefined;
  paddingRight: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  className: string;
  inputRef: React.RefObject<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  autoComplete?: string;
  props: React.InputHTMLAttributes<HTMLInputElement>;
}>) {
  const fixAutofillStyling = (element: HTMLInputElement) => {
    if (disabled) {
      return;
    }
    applyAutofillStyling(element, textColor, backgroundColor);
  };

  const handleDisabledClick: React.MouseEventHandler<HTMLInputElement> = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEnabledClick: React.MouseEventHandler<HTMLInputElement> = e => {
    fixAutofillStyling(e.currentTarget);
  };

  return (
    <input
      disabled={disabled}
      type={isPasswordField && showPassword ? 'text' : type}
      style={
        {
          width: '100%',
          height: '36px',
          padding: '8px 12px',
          paddingRight,
          backgroundColor,
          background: backgroundColor,
          backgroundImage: 'none',
          border: `1px solid ${borderColor}`,
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: 400,
          color: textColor,
          outline: 'none',
          boxShadow: `inset 0px 2px 4px 0px rgba(0,0,0,0.06), 0 0 0 1000px ${backgroundColor} inset`,
          WebkitTextFillColor: textColor,
          WebkitBoxShadow: `0 0 0px 1000px ${backgroundColor} inset`,
          cursor: disabled ? 'not-allowed' : 'text',
          pointerEvents: disabled ? 'none' : 'auto',
          WebkitAppearance: 'none',
          appearance: 'none',
          MozAppearance: 'textfield',
        } as React.CSSProperties
      }
      className={className}
      ref={inputRef}
      onFocus={e => {
        if (!disabled) {
          e.currentTarget.style.borderColor = 'var(--accent-primary)';
          fixAutofillStyling(e.currentTarget);
        }
      }}
      onAnimationStart={e => {
        if (e.animationName === 'onAutoFillStart' || e.animationName === 'autofill') {
          fixAutofillStyling(e.currentTarget);
        }
      }}
      onBlur={e => {
        e.currentTarget.style.borderColor = borderColor;
        e.currentTarget.style.border = `1px solid ${borderColor}`;
        fixAutofillStyling(e.currentTarget);
        onBlur?.(e);
      }}
      onInput={e => {
        if (disabled) {
          e.preventDefault();
          return;
        }
        e.currentTarget.style.borderColor = borderColor;
        e.currentTarget.style.border = `1px solid ${borderColor}`;
        fixAutofillStyling(e.currentTarget);
      }}
      onChange={disabled ? undefined : props.onChange}
      onClick={disabled ? handleDisabledClick : handleEnabledClick}
      autoFocus={undefined}
      autoComplete={autoComplete || (isPasswordField ? 'new-password' : 'off')}
      {...props}
    />
  );
}

function FormFieldTrailingIcon({
  isPasswordField,
  showPassword,
  disabled,
  rightIcon,
  onTogglePassword,
}: Readonly<{
  isPasswordField: boolean;
  showPassword: boolean;
  disabled: boolean;
  rightIcon?: React.ReactNode;
  onTogglePassword: () => void;
}>) {
  if (isPasswordField) {
    return (
      <PasswordToggle
        showPassword={showPassword}
        disabled={disabled}
        onToggle={onTogglePassword}
      />
    );
  }
  if (rightIcon) {
    return <RightIconSlot rightIcon={rightIcon} />;
  }
  return null;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  disabled = false,
  error,
  rightIcon,
  useInputBackground = false,
  className = '',
  type,
  autoFocus,
  onBlur,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isPasswordField = type === 'password';
  const backgroundColor = resolveBackgroundColor(disabled, useInputBackground);
  const borderColor = resolveBorderColor(error, disabled, useInputBackground);
  const textColor = '#E2E8F0';
  const paddingRight = isPasswordField || rightIcon ? '40px' : '12px';

  useAutofillFix(inputRef, disabled, backgroundColor, textColor);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
      }}
    >
      <FormFieldLabel
        label={label}
        required={required}
        disabled={disabled}
      />
      <div
        style={{
          position: 'relative',
          width: '100%',
          backgroundColor: disabled ? 'var(--bg-input-disabled)' : 'transparent',
          borderRadius: '6px',
        }}
      >
        <FormFieldInput
          disabled={disabled}
          isPasswordField={isPasswordField}
          showPassword={showPassword}
          type={type}
          paddingRight={paddingRight}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          textColor={textColor}
          className={className}
          inputRef={inputRef}
          onBlur={onBlur}
          autoComplete={props.autoComplete}
          props={props}
        />
        <FormFieldTrailingIcon
          isPasswordField={isPasswordField}
          showPassword={showPassword}
          disabled={disabled}
          rightIcon={rightIcon}
          onTogglePassword={() => setShowPassword(prev => !prev)}
        />
      </div>
      {error ? (
        <span
          style={{
            fontSize: '12px',
            color: '#dc2626',
            marginTop: '4px',
          }}
        >
          {error}
        </span>
      ) : null}
    </div>
  );
};

export default FormField;
